import { CSSProperties, memo } from 'react'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import Icon from 'elements/Icon'
import WebsiteBuilderPhonePreview from 'components/websiteBuilder/WebsiteBuilderPhonePreview'
import { CurrentBrand } from 'modules/Types'
import useBrand from 'Hooks/useBrand'

interface AssistantWorkspaceWebsitePreviewProps {
    workspaceContent: {
        type: 'report' | 'preview' | 'component' | 'custom' | 'website' | 'templateSelection';
        data: any;
        title?: string;
        isLoading?: boolean;
    };
    handleSendMessage: (message: string, hiddenData?: { templateData?: any }) => void;
}

const AssistantWorkspaceWebsitePreview = ({
    workspaceContent,
    handleSendMessage
}: AssistantWorkspaceWebsitePreviewProps) => {
    const { data } = workspaceContent; // data is activeWorkspace.data from useMaestroChat

    const {
        currentBrand
    } = useBrand()

    // Safety check for data existence and necessary demo fields
    if (!data || (data.isDemo && (!data.websiteContent || !data.websiteStyle))) {
        // If it's a demo and essential data is missing, show a specific demo error.
        // Otherwise, show the generic "No website data available".
        const message = (data?.isDemo && (!data.websiteContent || !data.websiteStyle))
            ? "Demo website data is incomplete."
            : "No website data available";

        return (
            <div className="assistantWorkspaceWebsite" style={sty.assistantWorkspaceWebsite}>
                <div className="assistantWorkspaceWebsitePreview" style={sty.assistantWorkspaceWebsitePreview}>
                    <div style={sty.assistantWorkspaceWebsitePlaceholder}>
                        <Icon
                            className="assistantWorkspaceWebsitePlaceholderIcon"
                            icon="website"
                            size={48}
                            style={{ color: Colors.greyDark }}
                        />
                        <div style={{ fontSize: 16, color: Colors.greyDark }}>
                            {message}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderWebsiteAdditionalPrompts = () => {
        // Generate contextual prompts based on website state
        const basePrompts = [
            { emoji: '🎵', text: 'Add a music player', action: 'Add a music player' },
            { emoji: '📱', text: 'Add social links', action: 'Add social media links' },
            { emoji: '✉️', text: 'Add email signup', action: 'Add an email signup form' },
            { emoji: '🎨', text: 'Change style', action: 'Change the colors and style' },
            { emoji: '📅', text: 'Add tour dates', action: 'Add tour dates section' },
            { emoji: '🖼️', text: 'Add images', action: 'Add an image gallery' },
            { emoji: '📝', text: 'Add text block', action: 'Add a text section with bio' },
            { emoji: '🎬', text: 'Add video', action: 'Add a video embed' }
        ];

        // Show 4 most relevant prompts for now
        const displayPrompts = basePrompts.slice(0, 4);

        const renderPromptButtons = () => {
            return displayPrompts.map((prompt, index) => (
                <button
                    key={index}
                    className="assistantWorkspaceWebsitePromptButton"
                    style={styx(
                        sty.assistantWorkspaceWebsitePromptButton,
                        workspaceContent?.isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                    )}
                    onClick={() => handleSendMessage(prompt.action)}
                    disabled={workspaceContent?.isLoading}
                    type="button"
                >
                    {prompt.emoji} {prompt.text}
                </button>
            ));
        };

        return (
            <div className="assistantWorkspaceWebsitePrompts" style={sty.assistantWorkspaceWebsitePrompts}>
                {renderPromptButtons()}
            </div>
        );
    }

    const renderWebsiteActions = () => {
        return (
            <div className="assistantWorkspaceWebsiteActions" style={sty.assistantWorkspaceWebsiteActions}>


                {/* Website Editor Button */}
                <button
                    className="assistantWorkspaceWebsiteEditorButton"
                    style={styx(
                        sty.assistantWorkspaceWebsiteEditorButton,
                        workspaceContent?.isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                    )}
                    disabled={workspaceContent?.isLoading}
                    onClick={() => {
                        if (data.slug && currentBrand?.slug) {
                            // Navigate to website editor using brand slug and website slug
                            window.open(`/website-builder/${currentBrand.slug}/${data.slug}`, '_blank');
                        }
                    }}
                    type="button"
                >
                    Edit in Website Editor
                </button>

                {/* Publish Button - only show for draft sites */}
                {data.status === 'DRAFT' && (
                    <button
                        className="assistantWorkspaceWebsitePublishButton"
                        style={styx(
                            sty.assistantWorkspaceWebsitePublishButton,
                            workspaceContent?.isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}
                        )}
                        disabled={workspaceContent?.isLoading}
                        onClick={() => handleSendMessage('Publish my website')}
                        type="button"
                    >
                        🚀 Publish Website
                    </button>
                )}
            </div>
        )
    }

    return (
        <div className="assistantWorkspaceWebsite" style={sty.assistantWorkspaceWebsite}>
            {/* Website Preview */}
            <div className="assistantWorkspaceWebsitePreview" style={sty.assistantWorkspaceWebsitePreview}>
                {workspaceContent?.isLoading ? (
                    <div style={sty.assistantWorkspaceWebsitePlaceholder}>
                        <Icon
                            className="assistantWorkspaceWebsitePlaceholderIcon"
                            icon="website"
                            size={48}
                            style={{ color: Colors.greyDark }}
                        />
                        <div style={{ fontSize: 16, color: Colors.greyDark }}>
                            Loading your website...
                        </div>
                    </div>
                ) : (
                    data.websiteContent && data.websiteStyle ? (
                        <WebsiteBuilderPhonePreview
                            className="assistantWorkspaceWebsitePhonePreview"
                            websiteContent={data.websiteContent.content}
                            websiteStyle={data.websiteStyle}
                            isSticky={false}
                            isPhone={true}
                            isFooter={false}
                        />
                    ) : (
                        <div style={sty.assistantWorkspaceWebsitePlaceholder}>
                            <Icon
                                className="assistantWorkspaceWebsitePlaceholderIcon"
                                icon="website"
                                size={48}
                                style={{ color: Colors.greyDark }}
                            />
                            <div style={{ fontSize: 16, color: Colors.greyDark }}>
                                Building your website...
                            </div>
                        </div>
                    )
                )}
            </div>

            {/* Website URL (only for published sites) */}
            {data.status === 'PUBLISHED' && data.slug && currentBrand?.slug && (
                <div className="assistantWorkspaceWebsiteUrl" style={sty.assistantWorkspaceWebsiteUrl}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                        🎉 Your website is live!
                    </div>
                    <a
                        href={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://symphony.is'}/${currentBrand.slug}/${data.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={sty.assistantWorkspaceWebsiteLink}
                    >
                        {`${process.env.NODE_ENV === 'development' ? 'localhost:3001' : 'symphony.is'}/${currentBrand.slug}/${data.slug}`}
                    </a>
                </div>
            )}
            {renderWebsiteActions()}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    /** Website workspace styles */
    assistantWorkspaceWebsite: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        height: '100%',
    },
    assistantWorkspaceWebsitePreview: {
        flex: 1,
        minHeight: 400,
        maxHeight: 700,
        backgroundColor: 'transparent',
        borderRadius: 8,
        overflow: 'visible',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
    },
    assistantWorkspaceWebsitePlaceholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 40,
    },
    assistantWorkspaceWebsiteActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    assistantWorkspaceWebsitePrompts: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    assistantWorkspaceWebsitePromptButton: {
        padding: '8px 12px',
        backgroundColor: Colors.greyLight,
        border: `1px solid ${Colors.grey}`,
        borderRadius: 6,
        fontSize: 14,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: Colors.textColor,
        fontFamily: 'inherit',
    },
    assistantWorkspaceWebsiteEditorButton: {
        padding: '12px 16px',
        backgroundColor: 'white',
        border: `2px solid ${Colors.purple}`,
        borderRadius: 8,
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: Colors.purple,
        fontFamily: 'inherit',
    },
    assistantWorkspaceWebsitePublishButton: {
        padding: '12px 16px',
        backgroundColor: Colors.purple,
        border: 'none',
        borderRadius: 8,
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        color: 'white',
        fontFamily: 'inherit',
    },
    assistantWorkspaceWebsiteUrl: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
        marginBottom: 16,
    },
    assistantWorkspaceWebsiteLink: {
        color: Colors.purple,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
    },
};

export default memo(AssistantWorkspaceWebsitePreview); 